/**
 * DateTime utility functions for formatting and manipulating dates
 *
 * TIMEZONE HANDLING STRATEGY:
 * - API Communication: All dates sent to the API are in UTC (using .toISOString())
 * - Display: All dates displayed to users use their local timezone (using toLocaleString/DateString/TimeString)
 * - Date Inputs: HTML5 date inputs are interpreted in the user's local timezone
 * - Date Calculations: Use UTC methods (setUTCHours, setUTCDate, etc.) for consistent calculations
 *
 * This ensures:
 * - API receives consistent UTC timestamps regardless of user location
 * - Users see dates/times in their familiar local timezone
 * - Date calculations are timezone-independent and consistent
 */

import { mathUtils } from "./utils.js";
import { Temporal } from "@js-temporal/polyfill";

export const ISO_STRING_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";

export const HOURS_24 = Temporal.Duration.from({ hours: 24 });
export const DAYS_7_IN_HOURS = Temporal.Duration.from({ hours: 168 });
export const DAYS_30_IN_HOURS = Temporal.Duration.from({ hours: 720 });
export const DAYS_60_IN_HOURS = Temporal.Duration.from({ hours: 1440 });
export const DAYS_365_IN_HOURS = Temporal.Duration.from({ hours: 8760 });

/**
 * Formats a date as "month day, year" (e.g., "Oct 8, 25")
 * @param {Date|string} date - The date to format
 * @returns {string} Formatted date string
 */
export const formatDateLong = (date) => {
  if (!date) return "";
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return "";
  return dateObj.toLocaleDateString("en-US", {
    year: "2-digit",
    month: "short",
    day: "numeric",
  });
};

/**
 * Private function that handles the core datetime formatting logic
 * @param {string|Date} date - The date to format
 * @param {Object} options - Formatting options
 * @param {string} options.nullValue - Value to return when date is null/undefined
 * @param {boolean} options.showJustNow - Whether to show "Just now" for very recent times
 * @param {boolean} options.showDays - Whether to show days in the format
 * @param {boolean} options.showDate - Whether to show full date for old times
 * @returns {string} Formatted time string
 */
const _formatRelativeTime = (date, options = {}) => {
  const {
    nullValue = "Never",
    showJustNow = false,
    showDays = false,
    showDate = false,
  } = options;

  if (!date) return nullValue;

  const target = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - target.getTime();
  const diffMinutes = mathUtils.floorDiv(diffMs, 1000 * 60);

  if (showJustNow && diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = mathUtils.floorDiv(diffMinutes, 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  if (showDays) {
    const diffDays = mathUtils.floorDiv(diffHours, 24);
    return `${diffDays}d ago`;
  }

  if (showDate) {
    // Display uses local timezone for user-friendly formatting
    return formatDateLong(target);
  }

  return `${diffHours}h ago`;
};

/**
 * Formats timestamp for display with relative time
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted timestamp (e.g., "2h ago", "Never")
 */
export const formatTimestamp = (date) => {
  return _formatRelativeTime(date, {
    nullValue: "Never",
    showJustNow: true,
    showDays: false,
    showDate: true,
  });
};

/**
 * Formats fault time for display with relative time
 * @param {string|Date} date - The fault date to format
 * @returns {string} Formatted fault time (e.g., "2h ago", "No faults")
 */
export const formatFaultTime = (date) => {
  return _formatRelativeTime(date, {
    nullValue: "No faults",
    showJustNow: false,
    showDays: true,
    showDate: false,
  });
};

/**
 * Formats timestamp for search functionality
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted timestamp for search
 */
export const formatTimestampForSearch = (date) => {
  if (!date) return "";
  const now = new Date();
  const target = new Date(date);
  const diffMs = now.getTime() - target.getTime();
  const diffMinutes = mathUtils.floorDiv(diffMs, 1000 * 60);

  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = mathUtils.floorDiv(diffMinutes, 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return formatDateLong(target).toLowerCase();
};

/**
 * Formats date/time as M/D/YYYY H:MM:SS AM/PM (e.g. "3/2/2026 4:44:56 PM")
 * @param {string|Date} date - Date or ISO string
 * @returns {string} Formatted string or "N/A" if invalid
 */
export const formatDateTimeWithSeconds = (date) => {
  if (!date) return "N/A";
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return "N/A";
  const datePart = dateObj.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
  const timePart = dateObj.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  return `${datePart} ${timePart}`;
};

const DATE_TIME_SHORT_OPTIONS = {
  year: "2-digit",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
};

function formatDateTimeShort(date) {
  const s = date.toLocaleString("en-US", DATE_TIME_SHORT_OPTIONS);
  return s
    .replace(/,(?=\s*\d{1,2}:\d{2}\s*(?:AM|PM))/i, "")
    .replace(/\s+at\s+/i, " ");
}

/**
 * Formats start time to readable format for downtime events (same pattern as Downtime Details / timeline: `MMM d, yy h:mm AM/PM` in local time).
 * @param {string|Date} startTime - ISO string or date (UTC from API is fine)
 * @param {string} [invalidOrEmptyDisplay="N/A"] - Returned when input is missing or not a valid date
 * @returns {string} Formatted start time (user's local timezone)
 */
export const formatStartTime = (startTime, invalidOrEmptyDisplay = "N/A") => {
  if (!startTime) return invalidOrEmptyDisplay;
  const date = new Date(startTime);
  if (isNaN(date.getTime())) return invalidOrEmptyDisplay;
  return formatDateTimeShort(date);
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MS_PER_HOUR = 60 * 60 * 1000;

/**
 * How long before a later reference instant an earlier event occurred (e.g. alarm vs shutdown pill: `Nd before` / `Nh before`).
 * @param {number} referenceLaterMs - Reference time in ms (e.g. shutdown)
 * @param {number} eventEarlierMs - Earlier event in ms (e.g. alarm)
 * @param {string} [emptyLabel="—"] - When diff is invalid or negative
 * @returns {string}
 */
export const formatTimeBeforeShutdown = (
  referenceLaterMs,
  eventEarlierMs,
  emptyLabel = "—"
) => {
  const diffMs = referenceLaterMs - eventEarlierMs;
  if (!Number.isFinite(diffMs) || diffMs < 0) return emptyLabel;
  if (diffMs >= MS_PER_DAY) {
    const days = Math.floor(diffMs / MS_PER_DAY);
    return `${Math.max(1, days)}d before`;
  }
  const hours = Math.round(diffMs / MS_PER_HOUR);
  return `${hours}h before`;
};

/**
 * Formats time for chart axis display based on period
 * @param {Date|string} timestamp - The timestamp to format (UTC from API)
 * @param {string} period - The time period ("24h", "7d", "30d", "60d")
 * @returns {string} Formatted time for chart axis (displayed in user's local timezone)
 */
export const formatTimeAxis = (timestamp, period) => {
  const date = new Date(timestamp);

  switch (period) {
    case "24h":
      // Display time in user's local timezone (12-hour format)
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    case "7d":
      return formatDateTimeShort(date);
    case "30d":
    case "60d":
      // Display date in user's local timezone (e.g., "Jan 10, 25")
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "2-digit",
      });
    default:
      // Display time in user's local timezone (12-hour format)
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
  }
};

/**
 * Formats duration in human readable format
 * @param {number} minutes - Duration in minutes
 * @param {boolean} isOngoing - Whether the duration is ongoing
 * @returns {string} Formatted duration (e.g., "2h 30m", "Ongoing")
 */
export const formatDuration = (minutes, isOngoing = false) => {
  if (isOngoing) return "Ongoing";
  if (minutes < 60) return `${mathUtils.round(minutes)}m`;

  const hours = mathUtils.floorDiv(minutes, 60);
  const remainingMinutes = mathUtils.round(minutes % 60);

  if (hours < 24) {
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  }

  const days = mathUtils.floorDiv(hours, 24);
  const remainingHours = hours % 24;

  if (days < 7) {
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
  }

  const weeks = mathUtils.floorDiv(days, 7);
  const remainingDays = days % 7;

  return remainingDays > 0 ? `${weeks}w ${remainingDays}d` : `${weeks}w`;
};

/**
 * Gets time ago string for last updated counter
 * @param {Date|string} lastTime - The last update time
 * @returns {string} Time ago string
 */
export const getTimeAgo = (lastTime) => {
  if (!lastTime) return "Never";

  const now = new Date();
  const last = new Date(lastTime);
  const diffMs = now.getTime() - last.getTime();
  const diffMinutes = mathUtils.floorDiv(diffMs, 1000 * 60);

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} min ago`;

  const diffHours = mathUtils.floorDiv(diffMinutes, 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;

  const diffDays = mathUtils.floorDiv(diffHours, 24);
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;

  const diffWeeks = mathUtils.floorDiv(diffDays, 7);
  return `${diffWeeks} week${diffWeeks > 1 ? "s" : ""} ago`;
};

/**
 * Formats time since last status change for display
 * Shows elapsed time since the status last changed (e.g., "5 min", "2h", "3d")
 * @param {string|Date} statusChangeTime - The timestamp when status last changed
 * @returns {string} Formatted time string (e.g., "5 min", "2h", "3d", "Never")
 */
export const formatTimeSinceStatusChange = (statusChangeTime) => {
  if (!statusChangeTime) return "Never";

  const target = new Date(statusChangeTime);
  const now = new Date();
  const diffMs = now.getTime() - target.getTime();
  const diffMinutes = mathUtils.floorDiv(diffMs, 1000 * 60);

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} min`;

  const diffHours = mathUtils.floorDiv(diffMinutes, 60);
  if (diffHours < 24) return `${diffHours}h`;

  const diffDays = mathUtils.floorDiv(diffHours, 24);
  if (diffDays < 7) return `${diffDays}d`;

  const diffWeeks = mathUtils.floorDiv(diffDays, 7);
  return `${diffWeeks}w`;
};

const OFFSET_REGEX = /^(\d+)([hdm])$/;
const OFFSET_ERROR = (offsetType) =>
  `Invalid offsetType format: ${offsetType}. Expected format like "30d", "7h", "24h"`;
const UNIT_ERROR = (unit) =>
  `Unsupported time unit: ${unit}. Supported units are h (hours), d (days), m (minutes)`;

function parseOffset(offsetType) {
  const match = offsetType.match(OFFSET_REGEX);
  if (!match) throw new Error(OFFSET_ERROR(offsetType));
  return { amount: parseInt(match[1], 10), unit: match[2] };
}

/**
 * Gets DateTimes that are offset from a source time (useful for date range APIs)
 * Uses UTC methods to ensure timezone-independent calculations
 * @param {Date|string} sourceTime - The time to take the offset from (expected to be UTC)
 * @param {string} offsetType - The type of offset to take (eg "7d", "24h", "30d")
 * @returns {Date} The offset date in UTC (use .toISOString() when sending to API)
 */
export const getDateOffset = (sourceTime, offsetType) => {
  if (!sourceTime || !offsetType) return undefined;
  const { amount, unit } = parseOffset(offsetType);
  const offsetTime = new Date(sourceTime);

  switch (unit) {
    case "h":
      offsetTime.setUTCHours(offsetTime.getUTCHours() - amount);
      break;
    case "d":
      offsetTime.setUTCDate(offsetTime.getUTCDate() - amount);
      break;
    case "m":
      offsetTime.setUTCMinutes(offsetTime.getUTCMinutes() - amount);
      break;
    default:
      throw new Error(UNIT_ERROR(unit));
  }
  return offsetTime;
};

/**
 * Gets DateTimes that are offset from a source time (useful for date range APIs)
 * @param {Temporal.ZonedDateTime} sourceTime - The time to take the offset from (expected to be UTC)
 * @param {string} offsetType - The type of offset to take (eg "7d", "24h", "30d")
 * @returns {Temporal.ZonedDateTime} The offset date in UTC (use .toISOString() when sending to API)
 */
export const getTemporalDateOffset = (sourceTime, offsetType) => {
  if (!sourceTime || !offsetType) return undefined;
  const { amount, unit } = parseOffset(offsetType);
  let duration;
  switch (unit) {
    case "h":
      duration = Temporal.Duration.from({ hours: amount });
      break;
    case "d":
      duration = Temporal.Duration.from({ days: amount });
      break;
    case "m":
      duration = Temporal.Duration.from({ minutes: amount });
      break;
    default:
      throw new Error(UNIT_ERROR(unit));
  }
  return sourceTime.subtract(duration);
};

/**
 * Format Date to CSV string
 * @param {Temporal.ZonedDateTime} dateToFormat - JavaScript Temporal.ZonedDateTime
 * @returns {string} String of Date formatted for CSV and Excel
 */
export const formatDateForCSV = (dateToFormat) =>
  `${dateToFormat.toPlainDate()} ${dateToFormat.toPlainTime().toString({ smallestUnit: "second" })}`;

/**
 * Rewrites a UTC ISO 8601 instant as an ISO string in the runtime local time zone
 * (same instant, includes offset). Omits the IANA time zone name suffix so the
 * result remains parseable by `new Date()`.
 *
 * @param {unknown} dateTimeUtc
 * @returns {string}
 */
export function utcIsoToLocalIso(dateTimeUtc) {
  const s = String(dateTimeUtc ?? "").trim();
  if (!s) return s;
  const tz = Temporal.Now.timeZoneId();
  try {
    const instant = Temporal.Instant.from(s);
    return instant.toZonedDateTimeISO(tz).toString({
      smallestUnit: "millisecond",
      timeZoneName: "never",
    });
  } catch {
    const ms = Date.parse(s);
    if (!Number.isFinite(ms)) return s;
    return Temporal.Instant.fromEpochMilliseconds(ms)
      .toZonedDateTimeISO(tz)
      .toString({
        smallestUnit: "millisecond",
        timeZoneName: "never",
      });
  }
}
